import { useCallback, useSyncExternalStore } from 'react';

/**
 * The phone tier: up to 720px wide, plus a phone held sideways (short and
 * landscape, but no wider than a tablet, so a short desktop window keeps its
 * sidebar). Mirrors the `phone` media queries in `src/index.css`.
 */
export const PHONE_QUERY =
  '(max-width: 720px), (max-height: 500px) and (orientation: landscape) and (max-width: 1024px) and (pointer: coarse)';

/** The tablet tier: the icon rail. Excludes a landscape phone, which is a phone. */
export const TABLET_QUERY = '(min-width: 721px) and (max-width: 1099px) and (min-height: 501px), (min-width: 721px) and (max-width: 1099px) and (pointer: fine)';

/** A finger rather than a mouse: decides sizes, independent of width. */
export const COARSE_QUERY = '(pointer: coarse)';

/**
 * Whether a media query matches, kept live as the window or device changes.
 *
 * Read through `useSyncExternalStore`, the same shape as `useReducedMotion`: a
 * media query is an outside value that changes on its own (a rotation, a
 * resized window), and a value captured once would go stale.
 *
 * Anything CSS can decide should stay in CSS; this is for behaviour and copy
 * that has to follow the tier (a tooltip only the rail needs, a panel that
 * steps aside on a phone).
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );
  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query]);
  return useSyncExternalStore(subscribe, getSnapshot);
}
